/**
 * The per-integration facts the connector manifest cannot carry: how it
 * connects and a sentence for the card. Everything else on the
 * Integrations pages — role, maturity, actions, per-Client health — comes
 * from the API, so a new integration is one connector registry entry on
 * the backend and one entry here (task 69).
 */

import type { ClientTenant, IntegrationName } from '@bluesphere/api-client';

import type { ConnectionState } from '../components/StatusBadge';

/**
 * `enable`: rides another integration's consent, but is switched on per
 * Client with an explicit Enable — Entra ID sign-in & audit logs (task 157), which needs a
 * permission a Client may not have granted yet and costs storage to run.
 */
/**
 * `mapping`: a platform credential stored once, and each Client mapped to one of
 * the vendor's organizations — NinjaOne and ConnectSecure (task 161).
 */
/**
 * `key`: any number of sources per Client, each posting with its own ingest
 * key — generic JSON (task 129; its page is task 211).
 */
export type ConnectsBy = 'consent' | 'token' | 'enable' | 'mapping' | 'key';

export interface IntegrationPage {
  description: string;
  connectsBy: ConnectsBy;
  /** One line under the connection controls saying what a credential is. */
  credentialNote: string;
  /**
   * Story 19: a token integration whose response actions need a second,
   * write-scoped credential gets Add / Rotate / Remove for it, beside the
   * read token's controls. Absent: the connection credential is enough.
   */
  responseCredential?: { label: string; note: string };
  /**
   * Task 82: this integration has no consent of its own — it is covered by
   * another integration's. There is one link to mint, and it belongs to
   * that one, so this page shows the state and offers no invite.
   */
  ridesConsentOf?: IntegrationName;
}

/**
 * The integrations this console has a page for. The LatticeAI DLP source is
 * created once per Client through its own route (task 180) and listed on the
 * generic sources page, so it has no card of its own.
 */
export type PagedIntegrationName = Exclude<IntegrationName, 'latticeai_dlp'>;

export const INTEGRATION_PAGES: Record<PagedIntegrationName, IntegrationPage> = {
  defender: {
    description:
      "Microsoft Defender XDR alerts and incidents, read through Microsoft Graph after the Client's admin consents to BlueSphere's app in their own Entra tenant.",
    connectsBy: 'consent',
    credentialNote:
      'No credential is stored per Client: access is the admin consent itself. Re-mint the invite if a pending link expired.',
  },
  sentinelone: {
    description:
      "SentinelOne threats, polled from the Client's console with a read-only Service User token scoped to their site.",
    connectsBy: 'token',
    credentialNote:
      'The token is validated against the console before it is stored, kept under the Client\'s own secret path, and never shown again. Rotate replaces it in place.',
    responseCredential: {
      label: 'response token',
      note: "A second Service User token with the agent-actions permission, for disconnecting and reconnecting endpoints (Story 19). Stored under its own secret path; the read-only ingestion token is untouched.",
    },
  },
  entra: {
    description:
      "Microsoft Entra ID as a place to act, not a place to read: lock a compromised user down — disable the account and revoke its sign-in sessions — and release them again. The user comes from the detections you already have; nothing here is polled.",
    connectsBy: 'consent',
    ridesConsentOf: 'defender',
    credentialNote:
      'No connection of its own: the same admin consent that connects Defender covers this, through the same app. A Client that consented before the user permissions existed shows them as not yet granted until Update permissions is used.',
  },
  entra_logs: {
    description:
      "Microsoft Entra ID sign-in and directory audit logs, read through Microsoft Graph every five minutes and kept 90 days for search (Story 24). Sign-ins need Entra ID P1 or P2; directory audits come with every tier.",
    connectsBy: 'enable',
    ridesConsentOf: 'defender',
    credentialNote:
      'No credential of its own: it rides the Defender consent, and needs AuditLog.Read.All in it. A Client that consented before that permission existed uses Update permissions first. Enabling reads the last seven days.',
  },
  ninjaone: {
    description:
      "NinjaOne, Simulint's RMM: device context for each Client's assets (Story 42). Tickets are bluesphere-ticketing's.",
    connectsBy: 'mapping',
    credentialNote:
      "One API Services application for the platform, validated before it is stored and never shown again. Each Client is then mapped to its NinjaOne organization; one organization can belong to one Client only.",
  },
  generic: {
    description:
      'Anything that can POST JSON: a forwarder, a script, a vendor webhook. Each source has its own ingest key and its own field mapping to OCSF, and lands in the event store like every other feed (Story 30).',
    connectsBy: 'key',
    credentialNote:
      "Each source's key is shown once, when it is created or rotated, and only its hash is stored.",
  },
  connectsecure: {
    description:
      "ConnectSecure vulnerability scanning: each asset's known vulnerabilities, known-exploited count and fixes, shown on detection and alert detail (Story 43).",
    connectsBy: 'mapping',
    credentialNote:
      "One API key for the platform, validated before it is stored and never shown again. Each Client is then mapped to its ConnectSecure company; one company can belong to one Client only.",
  },
};

/** The Graph permission Entra ID sign-in & audit logs needs in the Client's consent. */
export const ENTRA_LOGS_SCOPE = 'AuditLog.Read.All';

export function isIntegrationName(name: string): name is PagedIntegrationName {
  return name in INTEGRATION_PAGES;
}

/** Where one Client stands with one integration. */
export function connectionState(tenant: ClientTenant, name: PagedIntegrationName): ConnectionState {
  if (tenant.status === 'revoked') return 'revoked';
  const registration = tenant.sources[name];
  if (registration?.state === 'removed') return 'removed';
  if (INTEGRATION_PAGES[name].connectsBy === 'enable') {
    // Consent first, then an explicit Enable: the registration is the connection.
    if (tenant.status === 'active') return 'not_connected';
    if (tenant.status !== 'consented') return 'pending';
    return registration?.state === 'enabled' ? 'connected' : 'not_connected';
  }
  if (name === 'defender' || INTEGRATION_PAGES[name].ridesConsentOf === 'defender') {
    // Consent is the connection; the registration is implicit.
    if (tenant.status === 'active') return 'not_connected';
    return tenant.status === 'consented' ? 'connected' : 'pending';
  }
  return registration?.state === 'enabled' ? 'connected' : 'not_connected';
}
